<template>
  <section class="hero" id="home">
    <!-- Animated background orbs -->
    <div class="orb orb-1"></div>
    <div class="orb orb-2"></div>
    <div class="orb orb-3"></div>
    <div class="grid-pattern"></div>

    <div class="container hero-container">
      <!-- Left Content -->
      <div class="hero-content">
        <div class="hero-badge">
          <span class="badge-dot"></span>
          <span>一站式企业AI应用平台，现已开放体验</span>
        </div>

        <h1 class="hero-title">
          <span class="title-line">把工作交给<span class="gradient-text">点石</span></span>
          <span class="title-line">把时间还给<span class="gradient-text">自己</span></span>
        </h1>
        <h2 class="hero-subtitle">一站式企业AI应用平台</h2>

        <p class="hero-desc">
          海量工作流商店 + 专业AI智能体集群。在这里，<br class="br-hide">每一个想法都能找到它的执行引擎。
        </p>

        <!-- Highlight cards -->
        <div class="highlights">
          <div v-for="(item, i) in highlights" :key="i" class="highlight-card" :style="{ animationDelay: (0.1 + i * 0.15) + 's' }">
            <div class="highlight-icon" :style="{ background: item.color }">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2.2" stroke-linecap="round">
                <path :d="item.icon"/>
              </svg>
            </div>
            <div>
              <div class="highlight-title">{{ item.title }}</div>
              <div class="highlight-subtitle">{{ item.sub }}</div>
            </div>
          </div>
        </div>

        <!-- Stats -->
        <div class="stats-row">
          <div v-for="(stat, i) in stats" :key="i" class="stat-item">
            <div class="stat-num">{{ stat.num }}</div>
            <div class="stat-label">{{ stat.label }}</div>
          </div>
        </div>

        <!-- CTA Buttons -->
        <div class="hero-cta">
          <router-link :to="WORKFLOW_STORE" class="btn-primary hero-btn">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><polygon points="5 3 19 12 5 21 5 3"/></svg>
            立即体验
          </router-link>
          <a href="#" class="btn-outline hero-btn">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
            预约课程
          </a>
        </div>

        <!-- Trust badges -->
        <div class="trust-row">
          <span class="trust-item">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="var(--success)" stroke="none"><path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>
            免费注册
          </span>
          <span class="trust-item">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="var(--success)" stroke="none"><path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>
            无需信用卡
          </span>
          <span class="trust-item">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="var(--success)" stroke="none"><path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>
            即刻开始使用
          </span>
        </div>
      </div>

      <!-- Right: Product Mockup -->
      <div class="hero-visual">
        <div class="mockup-wrapper">
          <div class="mockup-card main-card">
            <!-- Mock top bar -->
            <div class="mock-topbar">
              <div class="mock-dots">
                <span style="background:#FF5F57"></span>
                <span style="background:#FEBC2E"></span>
                <span style="background:#28C840"></span>
              </div>
              <div class="mock-title">点石引擎 · 智能工作台</div>
              <div class="mock-actions">
                <div class="mock-badge live">实时运行中</div>
              </div>
            </div>

            <!-- Mock Chat Interface -->
            <div class="mock-chat">
              <div class="chat-msg user">请帮我生成一份产品推广短视频脚本</div>
              <div class="chat-msg ai">
                <div class="ai-avatar">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2.5"><path d="M12 2a10 10 0 110 20 10 10 0 010-20z"/><path d="M12 6v6l4 2"/></svg>
                </div>
                <div>
                  <div class="ai-tag">点石引擎 正在生成...</div>
                  <div class="typing-bar"><span></span><span></span><span></span></div>
                </div>
              </div>
              <div class="chat-result">
                <div class="result-header">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--success)" stroke-width="2.5"><polyline points="20 6 9 17 4 12"/></svg>
                  生成完成 · 爆款短视频脚本
                </div>
                <div class="result-lines">
                  <div class="result-line" style="width:90%"></div>
                  <div class="result-line" style="width:75%"></div>
                  <div class="result-line" style="width:85%"></div>
                  <div class="result-line" style="width:60%"></div>
                </div>
              </div>
            </div>

            <!-- Mock Stats row -->
            <div class="mock-stats">
              <div class="mock-stat-item" v-for="s in mockStats" :key="s.label">
                <div class="mock-stat-icon" :style="{ background: s.bg }">
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2.5"><path :d="s.icon"/></svg>
                </div>
                <div class="mock-stat-info">
                  <div class="mock-stat-val">{{ s.val }}</div>
                  <div class="mock-stat-label">{{ s.label }}</div>
                </div>
              </div>
            </div>
          </div>

          <!-- Floating mini cards -->
          <div class="float-card float-1">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--primary)" stroke-width="2.5"><polyline points="22 7 13.5 15.5 8.5 10.5 2 17"/><polyline points="16 7 22 7 22 13"/></svg>
            <div>
              <div class="float-val">20×</div>
              <div class="float-label">效率提升</div>
            </div>
          </div>

          <div class="float-card float-2">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#10B981" stroke-width="2.5"><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 00-3-3.87"/><path d="M16 3.13a4 4 0 010 7.75"/></svg>
            <div>
              <div class="float-val" style="color:#10B981">500+</div>
              <div class="float-label">智能体</div>
            </div>
          </div>

          <div class="float-card float-3">
            <div class="progress-ring-wrap">
              <svg width="40" height="40" viewBox="0 0 40 40">
                <circle cx="20" cy="20" r="16" fill="none" stroke="var(--border)" stroke-width="3"/>
                <circle cx="20" cy="20" r="16" fill="none" stroke="var(--primary)" stroke-width="3"
                  stroke-dasharray="100 100" stroke-dashoffset="25" stroke-linecap="round"
                  transform="rotate(-90 20 20)"/>
              </svg>
              <span class="ring-text">75%</span>
            </div>
            <div class="float-label" style="font-size:0.7rem;margin-top:4px">任务完成率</div>
          </div>
        </div>
      </div>
    </div>

    <!-- Scroll indicator -->
    <div class="scroll-indicator">
      <div class="scroll-mouse">
        <div class="scroll-wheel"></div>
      </div>
      <span>向下探索</span>
    </div>
  </section>
</template>

<script>
import { WORKFLOW_STORE } from '@/constants/homeNav'

export default {
  name: 'HeroSection',
  data() {
    return {
      WORKFLOW_STORE,
      highlights: [
        { title: '工作流商店', sub: '智慧的货架', icon: 'M3 7h18M3 12h18M3 17h12', color: 'linear-gradient(135deg,#2563EB,#06B6D4)' },
        { title: '智能体工坊', sub: '你的全能同事', icon: 'M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2M9 7a4 4 0 100 8 4 4 0 000-8zM23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75', color: 'linear-gradient(135deg,#7C3AED,#A855F7)' },
        { title: '办公助手', sub: '会思考的入口', icon: 'M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z', color: 'linear-gradient(135deg,#0EA5E9,#10B981)' },
      ],
      stats: [
        { num: '1000+', label: '专业工作流' },
        { num: '500+', label: '即插即用智能体' },
        { num: '20×', label: '工作效率提升' },
        { num: '1000+', label: '优质合作伙伴' },
      ],
      mockStats: [
        { label: '工作流', val: '1,024', bg: 'linear-gradient(135deg,#2563EB,#60A5FA)', icon: 'M3 7h18M3 12h18M3 17h12' },
        { label: '智能体', val: '537', bg: 'linear-gradient(135deg,#7C3AED,#A855F7)', icon: 'M12 2a10 10 0 110 20 10 10 0 010-20z' },
        { label: '今日任务', val: '248', bg: 'linear-gradient(135deg,#0EA5E9,#06B6D4)', icon: 'M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z' },
      ]
    }
  }
}
</script>

<style scoped>
.hero {
  position: relative;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  justify-content: center;
  overflow: hidden;
  background: var(--gradient-hero);
  padding-top: 72px;
}

/* Background orbs */
.orb {
  position: absolute;
  border-radius: 50%;
  pointer-events: none;
}
.orb-1 {
  width: 600px; height: 600px;
  background: radial-gradient(circle, rgba(37,99,235,0.15) 0%, transparent 70%);
  top: -200px; right: -150px;
  animation: float 8s ease-in-out infinite;
}
.orb-2 {
  width: 400px; height: 400px;
  background: radial-gradient(circle, rgba(6,182,212,0.12) 0%, transparent 70%);
  bottom: -100px; left: -100px;
  animation: float 10s ease-in-out infinite reverse;
}
.orb-3 {
  width: 300px; height: 300px;
  background: radial-gradient(circle, rgba(124,58,237,0.08) 0%, transparent 70%);
  top: 40%; left: 35%;
  animation: float 12s ease-in-out infinite;
}
.grid-pattern {
  position: absolute;
  inset: 0;
  background-image:
    linear-gradient(var(--border-light) 1px, transparent 1px),
    linear-gradient(90deg, var(--border-light) 1px, transparent 1px);
  background-size: 48px 48px;
  opacity: 0.4;
  pointer-events: none;
}

.hero-container {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 60px;
  align-items: center;
  padding-top: 48px;
  padding-bottom: 80px;
}

/* Left Content */
.hero-content {
  animation: fadeInLeft 0.8s ease-out both;
}

.hero-badge {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  background: var(--bg-card);
  border: 1.5px solid var(--border);
  border-radius: 100px;
  font-size: 0.82rem;
  font-weight: 500;
  color: var(--text-secondary);
  margin-bottom: 24px;
  box-shadow: var(--shadow-sm);
}
.badge-dot {
  width: 8px; height: 8px;
  border-radius: 50%;
  background: var(--success);
  box-shadow: 0 0 0 3px rgba(16,185,129,0.25);
  animation: pulse-ring 1.5s ease-out infinite;
  flex-shrink: 0;
}

.hero-title {
  font-size: clamp(2rem, 4.5vw, 3.5rem);
  font-weight: 900;
  line-height: 1.15;
  color: var(--text);
  margin-bottom: 12px;
  letter-spacing: -0.02em;
}
.title-line { display: block; }

.hero-subtitle {
  font-size: clamp(1rem, 2vw, 1.35rem);
  font-weight: 600;
  color: var(--text-secondary);
  margin-bottom: 16px;
  letter-spacing: 0.02em;
}

.hero-desc {
  font-size: 1rem;
  color: var(--text-muted);
  line-height: 1.8;
  margin-bottom: 32px;
}

/* Highlights */
.highlights {
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-bottom: 32px;
}
.highlight-card {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 14px 18px;
  background: var(--bg-card);
  border: 1.5px solid var(--border);
  border-radius: 14px;
  box-shadow: var(--shadow-sm);
  animation: fadeInUp 0.5s ease-out both;
  transition: all 0.25s ease;
}
.highlight-card:hover {
  transform: translateX(4px);
  border-color: var(--primary);
  box-shadow: var(--shadow);
}
.highlight-icon {
  width: 38px; height: 38px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.highlight-title {
  font-size: 0.9rem;
  font-weight: 700;
  color: var(--text);
}
.highlight-subtitle {
  font-size: 0.78rem;
  color: var(--text-muted);
}

/* Stats */
.stats-row {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 16px;
  margin-bottom: 28px;
}
.stat-item {
  text-align: center;
  padding: 12px;
  background: var(--bg-card);
  border: 1.5px solid var(--border);
  border-radius: 12px;
  box-shadow: var(--shadow-sm);
}
.stat-num {
  font-size: 1.35rem;
  font-weight: 800;
  background: var(--gradient-brand);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  line-height: 1.2;
}
.stat-label {
  font-size: 0.7rem;
  color: var(--text-muted);
  margin-top: 4px;
  font-weight: 500;
}

/* CTA */
.hero-cta {
  display: flex;
  align-items: center;
  gap: 14px;
  margin-bottom: 20px;
  flex-wrap: wrap;
}
.hero-btn {
  font-size: 0.95rem;
  padding: 14px 30px;
}

/* Trust row */
.trust-row {
  display: flex;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
}
.trust-item {
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 0.82rem;
  color: var(--text-muted);
  font-weight: 500;
}

/* Right Visual */
.hero-visual {
  display: flex;
  justify-content: center;
  align-items: center;
  animation: fadeInRight 0.8s ease-out 0.2s both;
}
.mockup-wrapper {
  position: relative;
  width: 100%;
  max-width: 480px;
}

.mockup-card {
  background: var(--bg-card);
  border: 1.5px solid var(--border);
  border-radius: 20px;
  overflow: hidden;
  box-shadow: var(--shadow-lg);
  animation: float 6s ease-in-out infinite;
}

.mock-topbar {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 18px;
  border-bottom: 1px solid var(--border);
  background: var(--bg-section);
}
.mock-dots { display: flex; gap: 6px; }
.mock-dots span { width: 12px; height: 12px; border-radius: 50%; }
.mock-title { flex: 1; font-size: 0.8rem; font-weight: 600; color: var(--text-secondary); text-align: center; }
.mock-badge.live {
  font-size: 0.7rem;
  padding: 3px 10px;
  background: rgba(16,185,129,0.15);
  color: var(--success);
  border-radius: 100px;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 4px;
}
.mock-badge.live::before {
  content: '';
  width: 6px; height: 6px;
  border-radius: 50%;
  background: var(--success);
  animation: pulse-ring 1.5s ease-out infinite;
  flex-shrink: 0;
}

.mock-chat {
  padding: 18px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.chat-msg {
  padding: 10px 14px;
  border-radius: 12px;
  font-size: 0.82rem;
  line-height: 1.5;
}
.chat-msg.user {
  background: var(--primary-light);
  color: var(--primary);
  align-self: flex-end;
  font-weight: 500;
}
.chat-msg.ai {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  background: transparent;
  padding: 0;
}
.ai-avatar {
  width: 28px; height: 28px;
  border-radius: 8px;
  background: var(--gradient-brand);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.ai-tag { font-size: 0.75rem; color: var(--text-muted); margin-bottom: 6px; font-weight: 500; }
.typing-bar { display: flex; gap: 4px; align-items: center; }
.typing-bar span {
  width: 6px; height: 6px;
  border-radius: 50%;
  background: var(--primary);
  animation: float 0.8s ease-in-out infinite;
}
.typing-bar span:nth-child(2) { animation-delay: 0.15s; background: var(--secondary); }
.typing-bar span:nth-child(3) { animation-delay: 0.3s; background: var(--accent); }

.chat-result {
  background: var(--bg-section);
  border: 1px solid var(--border);
  border-radius: 12px;
  padding: 12px 14px;
}
.result-header {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 0.78rem;
  font-weight: 600;
  color: var(--success);
  margin-bottom: 10px;
}
.result-lines { display: flex; flex-direction: column; gap: 7px; }
.result-line {
  height: 8px;
  background: var(--border);
  border-radius: 4px;
  background: linear-gradient(90deg, var(--border) 0%, var(--border-light) 50%, var(--border) 100%);
  background-size: 200% 100%;
  animation: shimmer 2s infinite;
}

.mock-stats {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1px;
  border-top: 1px solid var(--border);
  background: var(--border);
}
.mock-stat-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 14px;
  background: var(--bg-card);
}
.mock-stat-icon {
  width: 28px; height: 28px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.mock-stat-val { font-size: 0.85rem; font-weight: 700; color: var(--text); }
.mock-stat-label { font-size: 0.65rem; color: var(--text-muted); }

/* Floating cards */
.float-card {
  position: absolute;
  background: var(--bg-card);
  border: 1.5px solid var(--border);
  border-radius: 14px;
  padding: 12px 16px;
  box-shadow: var(--shadow);
  display: flex;
  align-items: center;
  gap: 10px;
  animation: floatSlow 5s ease-in-out infinite;
}
.float-1 { top: -20px; left: -30px; animation-delay: 1s; }
.float-2 { bottom: 40px; left: -40px; animation-delay: 2.5s; }
.float-3 { top: 30%; right: -30px; animation-delay: 0.5s; flex-direction: column; align-items: center; padding: 10px; }
.float-val { font-size: 1rem; font-weight: 800; color: var(--primary); line-height: 1; }
.float-label { font-size: 0.68rem; color: var(--text-muted); }
.progress-ring-wrap { position: relative; display: flex; align-items: center; justify-content: center; }
.ring-text { position: absolute; font-size: 0.65rem; font-weight: 700; color: var(--primary); }

/* Scroll indicator */
.scroll-indicator {
  position: absolute;
  bottom: 32px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  color: var(--text-muted);
  font-size: 0.75rem;
  animation: float 2s ease-in-out infinite;
}
.scroll-mouse {
  width: 24px; height: 36px;
  border: 2px solid var(--border);
  border-radius: 12px;
  display: flex;
  justify-content: center;
  padding-top: 6px;
}
.scroll-wheel {
  width: 4px; height: 8px;
  background: var(--primary);
  border-radius: 2px;
  animation: float 1.5s ease-in-out infinite;
}

.br-hide { display: none; }

@media (min-width: 768px) { .br-hide { display: inline; } }

@media (max-width: 1024px) {
  .hero-container { grid-template-columns: 1fr; gap: 48px; }
  .hero-visual { display: none; }
  .stats-row { grid-template-columns: repeat(2, 1fr); }
  .hero { min-height: auto; padding-bottom: 40px; }
  .scroll-indicator { display: none; }
}
@media (max-width: 640px) {
  .stats-row { grid-template-columns: repeat(2, 1fr); }
  .hero-cta { flex-direction: column; align-items: flex-start; }
  .float-1, .float-2, .float-3 { display: none; }
}
</style>
